import { useCallback, useEffect, useRef } from 'react';
import type { StorageConfig } from '../services';
import { StorageService } from '../services';

export interface StorageHookResult {
  setItem: <T>(key: string, value: T, expirationInMs?: number) => void;
  getItem: <T>(key: string) => T | null;
  removeItem: (key: string) => void;
  clear: () => void;
  isSupported: boolean;
}

export interface StorageOptions extends StorageConfig {
  defaultExpiration?: number; // Default expiration in milliseconds
}

/**
 * Custom hook for managing persistent storage with expiration support
 *
 * @param options - Storage configuration options
 * @returns Storage management functions and state
 */
export function useStorage(
  options: StorageOptions = { type: 'localStorage' }
): StorageHookResult {
  const storageServiceRef = useRef<StorageService | null>(null);
  const { defaultExpiration } = options;

  // Initialize storage service
  useEffect(() => {
    storageServiceRef.current = new StorageService(options);
  }, [options]);

  const setItem = useCallback(
    <T>(key: string, value: T, expirationInMs?: number): void => {
      if (!storageServiceRef.current) {
        return;
      }

      const expiration = expirationInMs || defaultExpiration;
      const item = expiration
        ? {
            value,
            timestamp: Date.now(),
            expiration,
          }
        : value;

      storageServiceRef.current.setItem(key, item);
    },
    [defaultExpiration]
  );

  const getItem = useCallback(<T>(key: string): T | null => {
    if (!storageServiceRef.current) {
      return null;
    }

    const item = storageServiceRef.current.getItem<
      { value: T; timestamp: number; expiration: number } | T
    >(key);

    if (item === null || item === undefined) {
      return null;
    }

    // Check if item has expiration metadata
    if (
      typeof item === 'object' &&
      item !== null &&
      'timestamp' in item &&
      'expiration' in item &&
      'value' in item
    ) {
      const { value, timestamp, expiration } = item as {
        value: T;
        timestamp: number;
        expiration: number;
      };

      // Check if item has expired
      if (Date.now() - timestamp > expiration) {
        storageServiceRef.current.removeItem(key);
        return null;
      }

      return value;
    }

    // Return item as-is if no expiration metadata
    return item as T;
  }, []);

  const removeItem = useCallback((key: string): void => {
    if (!storageServiceRef.current) {
      return;
    }
    storageServiceRef.current.removeItem(key);
  }, []);

  const clear = useCallback((): void => {
    if (!storageServiceRef.current) {
      return;
    }
    storageServiceRef.current.clear();
  }, []);

  const isSupported = storageServiceRef.current?.isStorageSupported() ?? false;

  return {
    setItem,
    getItem,
    removeItem,
    clear,
    isSupported,
  };
}
